import { Box, chakra, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { LuLayers, LuLayoutGrid } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { themeLabel, type Companion } from "src/models/companion";
import { GradientButton } from "src/components/GradientButton";
import { CompanionAvatar } from "src/components/CompanionAvatar";
import { CompanionCard } from "src/components/CompanionCard";
import { CompanionSheet } from "src/components/CompanionSheet";
import { SwipeDeck } from "src/components/SwipeDeck";
import { Disclaimer, TAB_BAR_SPACE } from "src/layouts/AppLayout";
import { useChatStore } from "src/stores/chatStoreContext";
import { tap } from "src/utils/haptic";

/** "all", or a theme slug. */
type Filter = string;
type View = "deck" | "grid";

const VIEW_KEY = "truelovelica:discover-view";

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "deck";
  } catch {
    return "deck";
  }
}

function FilterChips({
  companions,
  value,
  onChange,
}: {
  companions: readonly Companion[];
  value: Filter;
  onChange: (f: Filter) => void;
}) {
  // Themes in the order they first appear in the catalogue.
  const themes = [...new Set(companions.map((c) => c.theme))];
  const options: { id: Filter; label: string }[] = [
    { id: "all", label: "Everyone" },
    ...themes.map((t) => ({ id: t, label: themeLabel(t) })),
  ];
  return (
    <Flex gap={2} overflowX="auto" px={5} py={1}>
      {options.map((o) => {
        const active = o.id === value;
        return (
          <chakra.button
            key={o.id}
            type="button"
            onClick={() => {
              tap();
              onChange(o.id);
            }}
            flexShrink={0}
            px={4}
            h="34px"
            borderRadius="full"
            fontSize="sm"
            fontWeight="semibold"
            bg={active ? "white" : "elevated"}
            color={active ? "black" : "rgba(255,255,255,0.8)"}
            transition="background 0.2s, color 0.2s, transform 0.15s"
            _active={{ transform: "scale(0.95)" }}
          >
            {o.label}
          </chakra.button>
        );
      })}
    </Flex>
  );
}

/** Story-style rings for the chats already going, newest first. */
const ContinueRow = observer(() => {
  const chatStore = useChatStore();
  const navigate = useNavigate();
  const recent = chatStore.recentChats;
  if (recent.length === 0) return null;

  return (
    <Flex gap={3} overflowX="auto" px={5} pb={1} mt={4}>
      {recent.map(({ companion }) => (
        <chakra.button
          key={companion.id}
          type="button"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          flexShrink={0}
          onClick={() => {
            tap();
            navigate(`/chat/${companion.id}`);
          }}
          _active={{ transform: "scale(0.94)" }}
          transition="transform 0.15s"
        >
          <CompanionAvatar companion={companion} size="54px" ring />
          <Text fontSize="11px" color="rgba(255,255,255,0.8)">
            {companion.name}
          </Text>
        </chakra.button>
      ))}
    </Flex>
  );
});

/** While the catalogue loads, or when it failed. */
const CatalogueState = observer(() => {
  const chatStore = useChatStore();
  if (chatStore.catalogueState === "error") {
    return (
      <Flex
        direction="column"
        align="center"
        textAlign="center"
        gap={3}
        px={8}
        pt={20}
      >
        <Text fontSize="lg" fontWeight="semibold">
          Couldn't load the companions
        </Text>
        <Text color="muted" fontSize="sm" mb={2}>
          Check your connection and try again.
        </Text>
        <GradientButton
          width="auto"
          px={8}
          onClick={() => void chatStore.loadAll()}
        >
          Try again
        </GradientButton>
      </Flex>
    );
  }
  return (
    <Box px={5} pt={4}>
      <Box
        h="min(60dvh, 520px)"
        borderRadius="30px"
        bg="surface"
        animation="tlGlow 1.4s ease-in-out infinite"
      />
    </Box>
  );
});

export const HomePage = observer(() => {
  const chatStore = useChatStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = React.useState<Filter>("all");
  const [view, setView] = React.useState<View>(readView);
  const open = chatStore.findCompanion(params.get("c") ?? undefined) ?? null;
  const companions = chatStore.companions;
  const ready = chatStore.catalogueState === "ready" && companions.length > 0;

  const list =
    filter === "all"
      ? companions
      : companions.filter((c) => c.theme === filter);

  // The sheet is in the URL, so the phone's back gesture closes it.
  const openSheet = React.useCallback(
    (c: Companion) => {
      tap();
      setParams({ c: c.id });
    },
    [setParams],
  );
  const closeSheet = React.useCallback(
    () => setParams({}, { replace: true }),
    [setParams],
  );
  const openChat = React.useCallback(
    (c: Companion) => navigate(`/chat/${c.id}`),
    [navigate],
  );

  function switchView() {
    tap();
    const next: View = view === "deck" ? "grid" : "deck";
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Not saved: the next visit shows the deck again.
    }
  }

  const isDeck = view === "deck";

  return (
    <Box
      pb={TAB_BAR_SPACE}
      // The deck fills the screen between the top bar and the tab bar.
      {...(isDeck && {
        h: "calc(100dvh - 70px - env(safe-area-inset-top))",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      })}
    >
      <Flex
        px={5}
        pt={1}
        align="center"
        justify="space-between"
        gap={3}
        flexShrink={0}
      >
        <Text
          fontSize="26px"
          fontWeight="bold"
          letterSpacing="-0.02em"
          lineHeight="1.15"
        >
          {isDeck ? "Who will you talk to?" : "All companions"}
        </Text>
        <chakra.button
          type="button"
          onClick={switchView}
          aria-label={
            view === "deck" ? "Show all as a grid" : "Show as a card deck"
          }
          boxSize="40px"
          borderRadius="full"
          bg="elevated"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          _active={{ transform: "scale(0.9)" }}
          transition="transform 0.15s"
        >
          {view === "deck" ? (
            <LuLayoutGrid size={18} />
          ) : (
            <LuLayers size={18} />
          )}
        </chakra.button>
      </Flex>

      {/* Chats in progress are on the Chats tab; the deck needs the room. */}
      {!isDeck && <ContinueRow />}

      <Box mt={4} flexShrink={0}>
        <FilterChips
          companions={companions}
          value={filter}
          onChange={setFilter}
        />
      </Box>

      {!ready ? (
        <CatalogueState />
      ) : isDeck ? (
        <Flex
          direction="column"
          flex="1"
          minH={0}
          px={5}
          pt={4}
          pb={2}
          key={`deck-${filter}`}
          animation="tlRise 0.4s ease both"
        >
          <SwipeDeck
            companions={list}
            onChat={openChat}
            onInfo={openSheet}
            storageKey={`truelovelica:deck:${filter}`}
          />
        </Flex>
      ) : (
        <>
          <SimpleGrid key={`grid-${filter}`} columns={2} gap={3} px={5} mt={5}>
            {list.map((c, i) => (
              <CompanionCard
                key={c.id}
                companion={c}
                onOpen={openSheet}
                index={i}
              />
            ))}
          </SimpleGrid>
          <Disclaimer />
        </>
      )}

      <CompanionSheet companion={open} onClose={closeSheet} />
    </Box>
  );
});
